import { useEffect, useMemo, useState } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Collapse, Select, Typography } from "antd";
import type {
  MetricOption,
  PlayerCardComponentSpec,
  PlayerCardOption,
  SubjectEntity,
} from "../../Processing/types";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { ToggleOption } from "../shared/ToggleOption";
import {
  PLAYER_CARD_OPTIONS,
  createInitialState,
  formStateToSpec,
  hydrateFromSpec,
  playerCardReducer,
} from "./playerCardState";
import { PlayerCardView } from "../../WidgetComponents/PlayerCardView";
import { buildPreviewPlayerCardData } from "./previewData";
import { ControllerBindingRow } from "../shared/ControllerBindingRow";
import { useControllers } from "../../Contexts/dashboardHooks";
import { SectionSummary } from "../shared/SectionSummary";
import { boundController, controllerAthlete } from "../../Processing/controllerBindings";

const { Panel } = Collapse;
const { OptGroup, Option } = Select;
const { Text } = Typography;

interface PlayerOption {
  value: number;
  name: string;
  detail: string;
}


export function PlayerCardConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<PlayerCardComponentSpec>) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: playerCardReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const [expandedKey, setExpandedKey] = useState<string | string[]>("__player");

  // Active players only (getSubjectEntities already filters retired === false).
  // In edit mode, a saved player who has since retired is injected so the
  // selection isn't wiped — their id round-trips even without a name.
  const playerOptions = useMemo<PlayerOption[]>(() => {
    const opts = getSubjectEntities("player").map((e) => ({
      value: Number(e.id),
      name: e.name,
      detail: e.detail,
    }));
    if (state.playerId != null && !opts.some((o) => o.value === state.playerId)) {
      opts.unshift({ value: state.playerId, name: `Retired player (#${state.playerId})`, detail: "" });
    }
    return opts;
  }, [getSubjectEntities, state.playerId]);

  // New widget: default to the first player so the card previews immediately.
  // Skipped in edit mode (existingSpec) and once a player is already chosen.
  useEffect(() => {
    if (existingSpec || state.playerId != null) return;
    const first = playerOptions[0];
    if (first) dispatch({ type: "SET_PLAYER", playerId: first.value });
  }, [existingSpec, state.playerId, playerOptions, dispatch]);

  const controllers = useControllers();
  const bound = boundController(state.singlePlayerControllerId, "single_player", controllers);
  const isBound = bound != null;
  // The controller names its athlete as `player-<id>`; the card stores the bare integer.
  const boundPlayerId = useMemo(() => {
    const subject = bound
      ? controllerAthlete(bound.controls, bound.spec.slicer_builder_misc)
      : "";
    const match = subject ? /^player-(\d+)$/.exec(subject) : null;
    return match ? Number(match[1]) : null;
  }, [bound]);

  const selectedPlayerName = useMemo(
    () => playerOptions.find((o) => o.value === (boundPlayerId ?? state.playerId))?.name ?? null,
    [playerOptions, state.playerId, boundPlayerId],
  );

  const selectedPlayerPosition = useMemo(
    () => playerOptions.find((o) => o.value === state.playerId)?.detail ?? null,
    [playerOptions, state.playerId],
  );

  const form = (
    <Collapse
      accordion
      activeKey={expandedKey}
      onChange={(key) => setExpandedKey(key)}
      style={{ borderRadius: 0 }}
    >
      <Panel
        key="__player"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">1</span>
            <Text strong style={{ fontSize: 13 }}>Player</Text>
            <SectionSummary kind="single_player" controllerId={state.singlePlayerControllerId}
              own={selectedPlayerName ?? "Required"} getSubjectEntities={getSubjectEntities} />
          </div>
        }
      >
        <div className="awm-step-body">
          <ControllerBindingRow
            kind="single_player"
            controllerId={state.singlePlayerControllerId}
            onChange={(controllerId) =>
              dispatch({ type: "SET_PLAYER_CONTROLLER", controllerId })}
          />
          <div className={`awm-form-stack${isBound ? " awm-bound-section" : ""}`}>
          <Select<number>
            showSearch
            placeholder="Select a player"
            optionFilterProp="label"
            value={boundPlayerId ?? state.playerId ?? undefined}
            onChange={(v) => dispatch({ type: "SET_PLAYER", playerId: v })}
            style={{ width: "100%" }}
          >
            <OptGroup label="Players">
              {playerOptions.map((p) => (
                <Option key={p.value} value={p.value} label={p.name}>
                  {p.name}
                  {p.detail && (
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      {p.detail}
                    </Text>
                  )}
                </Option>
              ))}
            </OptGroup>
          </Select>
          </div>
        </div>
      </Panel>

      <Panel
        key="__fields"
        header={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="awm-step-number">2</span>
            <Text strong style={{ fontSize: 13 }}>Display Options</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {`${state.options.length} selected`}
            </Text>
          </div>
        }
      >
        <div className="awm-step-body" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {PLAYER_CARD_OPTIONS.map((opt) => (
            <ToggleOption
              key={opt.key}
              label={opt.label}
              description={opt.description}
              checked={state.options.includes(opt.key)}
              onChange={() => dispatch({ type: "TOGGLE_OPTION", option: opt.key })}
            />
          ))}
        </div>
      </Panel>
    </Collapse>
  );

  const preview = (
    <PreviewShell
      title="Preview"
      hasContent={state.playerId != null}
      emptyText="Select a player to preview the card"
      summaryText={
        state.playerId == null
          ? "Select a player to enable this widget"
          : `${state.options.length} field${state.options.length === 1 ? "" : "s"} selected`
      }
    >
      <div className="pc-preview-center">
        <div className="pc-preview-frame">
          <PlayerCardView
            data={buildPreviewPlayerCardData(state.options, selectedPlayerName, selectedPlayerPosition)}
          />
        </div>
      </div>
    </PreviewShell>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
